/**
 * BulkLpUploadPage — fs811, bulk landing-page CSV importer.
 *
 * 2-step flow:
 *   1. Upload: user picks a CSV file + enters campaign id -> POST / returns
 *      a dry-run preview (jobId, parsed count, created count, row-level errors).
 *   2. Commit: user reviews preview table; clicking Commit re-fetches the job
 *      from GET /job/:jobId and swaps to the success state (rows live).
 *
 * No RTL / jsdom in this repo — page is exported plainly so it can be
 * server-rendered for smoke tests (see BulkLpUploadPage.test.tsx).
 */
import { useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import {
	usePreviewUpload,
	useJobDetail,
	useCampaignJobs,
} from "./hooks";
import { countCsvRows, hasRequiredCsvHeader } from "./api";

// ---------------------------------------------------------------------------
// Styling tokens — emerald->sky gradient headline, zinc-200 borders, 6-8px
// radii, Inter via app root. Plain Tailwind; no shadcn imports yet.
// ---------------------------------------------------------------------------

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50";
const PILL_OK = "rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200";
const PILL_ERR = "rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 border border-red-200";
const PILL_NEUTRAL = "rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 border border-zinc-200";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Step = "upload" | "preview" | "committed";

export function BulkLpUploadPage() {
	// form state
	const [campaignId, setCampaignId] = useState("");
	const [csvText, setCsvText] = useState("");
	const [fileName, setFileName] = useState<string | null>(null);
	const [readError, setReadError] = useState<string | null>(null);
	const [step, setStep] = useState<Step>("upload");
	const [jobId, setJobId] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	// mutations + queries
	const previewMut = usePreviewUpload();
	const jobQuery = useJobDetail(step === "committed" ? jobId : null);
	const campaignHistory = useCampaignJobs(campaignId.trim().length > 0 ? campaignId.trim() : null);

	// derived csv stats
	const csvRowCount = useMemo(() => countCsvRows(csvText), [csvText]);
	const csvHeaderOk = useMemo(() => csvText.length > 0 && hasRequiredCsvHeader(csvText), [csvText]);

	// ---- handlers ---------------------------------------------------------

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		setReadError(null);
		const file = e.currentTarget.files?.[0];
		if (!file) return;
		if (file.size > 2_000_000) {
			setReadError("File is larger than 2MB — please split it up.");
			return;
		}
		setFileName(file.name);
		const reader = new FileReader();
		reader.onload = () => {
			const txt = typeof reader.result === "string" ? reader.result : "";
			setCsvText(txt);
		};
		reader.onerror = () => setReadError("Could not read the file.");
		reader.readAsText(file);
	};

	const handlePreview = (e: FormEvent) => {
		e.preventDefault();
		setReadError(null);
		if (!campaignId.trim()) {
			setReadError("Campaign id is required.");
			return;
		}
		if (!csvText.trim()) {
			setReadError("Upload a CSV first, or paste rows below.");
			return;
		}
		previewMut.mutate(
			{ campaignId: campaignId.trim(), csvText },
			{
				onSuccess: (data) => {
					setJobId(data.jobId);
					setStep("preview");
				},
			},
		);
	};

	const handleCommit = () => {
		// Job is already saved server-side by POST /. Commit is a read-side
		// confirmation: move to committed step and render full job detail.
		setStep("committed");
	};

	const handleReset = () => {
		setCampaignId("");
		setCsvText("");
		setFileName(null);
		setReadError(null);
		setJobId(null);
		setStep("upload");
		previewMut.reset();
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	// ---- renderers --------------------------------------------------------

	const preview = previewMut.data ?? null;
	const historyJobs = campaignHistory.data?.jobs ?? [];

	return (
		<main
			data-testid="bulk-lp-upload-page"
			className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-10"
		>
			{/* Heading */}
			<header className="flex flex-col gap-2">
				<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					Campaigns / Bulk import
				</p>
				<h1
					className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent"
					data-testid="bulk-lp-upload-title"
				>
					Bulk landing page upload
				</h1>
				<p className="text-sm text-zinc-600">
					Upload a CSV of landing pages to attach to a campaign. We parse,
					dedupe by label, and show any row-level errors before you commit.
				</p>
			</header>

			{/* Step indicator */}
			<ol
				className="flex items-center gap-2 text-xs"
				aria-label="progress"
				data-testid="bulk-lp-upload-steps"
			>
				<StepPill label="1. Upload" active={step === "upload"} done={step !== "upload"} />
				<StepSep />
				<StepPill label="2. Review" active={step === "preview"} done={step === "committed"} />
				<StepSep />
				<StepPill label="3. Done" active={step === "committed"} done={step === "committed"} />
			</ol>

			{/* ------------------ Step 1: Upload form ------------------ */}
			{step === "upload" ? (
				<form
					onSubmit={handlePreview}
					className={`${CARD} flex flex-col gap-4`}
					data-testid="bulk-lp-upload-form"
					aria-label="bulk-lp-upload form"
				>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="bulk-lp-upload-campaign" className={LABEL}>
							Campaign id
						</label>
						<input
							id="bulk-lp-upload-campaign"
							name="campaignId"
							type="text"
							required
							autoComplete="off"
							placeholder="cmp_abc123"
							className={INPUT}
							value={campaignId}
							onChange={(e) => setCampaignId(e.currentTarget.value)}
							data-testid="bulk-lp-upload-campaign-input"
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="bulk-lp-upload-file" className={LABEL}>
							CSV file
						</label>
						<input
							id="bulk-lp-upload-file"
							ref={fileInputRef}
							type="file"
							accept=".csv,text/csv"
							onChange={handleFileChange}
							className="text-sm text-zinc-700 file:mr-3 file:rounded-[6px] file:border file:border-zinc-300 file:bg-zinc-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-zinc-700 hover:file:bg-zinc-100"
							data-testid="bulk-lp-upload-file-input"
						/>
						<p className="text-xs text-zinc-500">
							Columns required: <code>label</code> and <code>url</code>.
							Optional: <code>weight</code>, <code>is_default</code>.
						</p>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="bulk-lp-upload-csv" className={LABEL}>
							Or paste CSV
						</label>
						<textarea
							id="bulk-lp-upload-csv"
							name="csvText"
							rows={6}
							placeholder="label,url,weight,is_default"
							className={`${INPUT} font-mono`}
							value={csvText}
							onChange={(e) => setCsvText(e.currentTarget.value)}
							data-testid="bulk-lp-upload-csv-input"
						/>
					</div>

					{csvText.length > 0 ? (
						<div
							className="flex flex-wrap items-center gap-2 text-xs text-zinc-600"
							data-testid="bulk-lp-upload-csv-stats"
						>
							<span className={csvHeaderOk ? PILL_OK : PILL_ERR}>
								{csvHeaderOk ? "Header OK" : "Missing label/url column"}
							</span>
							<span className={PILL_NEUTRAL}>{csvRowCount} data rows</span>
							{fileName ? (
								<span className={PILL_NEUTRAL} data-testid="bulk-lp-upload-filename">
									{fileName}
								</span>
							) : null}
						</div>
					) : null}

					<div className="flex items-center gap-3">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={previewMut.isPending || !csvHeaderOk || !campaignId.trim()}
							data-testid="bulk-lp-upload-preview-btn"
						>
							{previewMut.isPending ? "Analyzing." : "Run dry-run"}
						</button>
						{previewMut.isPending ? (
							<span
								className="text-sm text-zinc-600"
								data-testid="bulk-lp-upload-progress"
								role="status"
							>
								Parsing CSV and validating rows.
							</span>
						) : null}
						{readError ? (
							<p className="text-sm text-red-600" role="alert" data-testid="bulk-lp-upload-inline-error">
								{readError}
							</p>
						) : null}
						{previewMut.error ? (
							<p className="text-sm text-red-600" role="alert" data-testid="bulk-lp-upload-mutation-error">
								{previewMut.error.message}
							</p>
						) : null}
					</div>
				</form>
			) : null}

			{/* ------------------ Step 2: Preview table ------------------ */}
			{step === "preview" && preview ? (
				<section
					className={`${CARD} flex flex-col gap-4`}
					data-testid="bulk-lp-upload-preview"
					aria-label="dry-run preview"
				>
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div className="flex flex-col gap-1">
							<h2 className="text-lg font-semibold text-zinc-900">Dry-run preview</h2>
							<p className="text-sm text-zinc-600">
								Job <code>{preview.jobId}</code> — review before you commit.
							</p>
						</div>
						<div className="flex items-center gap-2 text-xs">
							<span className={PILL_NEUTRAL}>Parsed: {preview.parsed}</span>
							<span className={PILL_OK}>Valid: {preview.created}</span>
							<span className={preview.error_count > 0 ? PILL_ERR : PILL_NEUTRAL}>
								Errors: {preview.error_count}
							</span>
						</div>
					</div>

					{preview.duplicate_labels.length > 0 ? (
						<div
							className="rounded-[6px] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
							data-testid="bulk-lp-upload-duplicate-labels"
						>
							Duplicate labels collapsed:{" "}
							{preview.duplicate_labels.map((l, i) => (
								<span key={l}>
									<code>{l}</code>
									{i < preview.duplicate_labels.length - 1 ? ", " : ""}
								</span>
							))}
						</div>
					) : null}

					{/* Row-level errors table */}
					{preview.errors.length > 0 ? (
						<div
							className="overflow-hidden rounded-[8px] border border-zinc-200"
							data-testid="bulk-lp-upload-errors-table"
						>
							<table className="w-full text-left text-sm">
								<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
									<tr>
										<th className="px-3 py-2">Row</th>
										<th className="px-3 py-2">Field</th>
										<th className="px-3 py-2">Code</th>
										<th className="px-3 py-2">Message</th>
									</tr>
								</thead>
								<tbody>
									{preview.errors.map((err, i) => (
										<tr
											key={`${err.row}-${err.code}-${i}`}
											className="border-t border-zinc-100"
										>
											<td className="px-3 py-2 font-mono text-xs text-zinc-700">
												{err.row}
											</td>
											<td className="px-3 py-2 text-zinc-700">{err.field ?? "-"}</td>
											<td className="px-3 py-2 text-zinc-700">
												<code className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs">
													{err.code}
												</code>
											</td>
											<td className="px-3 py-2 text-red-700">{err.message}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<p
							className="text-sm text-emerald-700"
							role="status"
							data-testid="bulk-lp-upload-no-errors"
						>
							No row-level errors. Ready to commit.
						</p>
					)}

					<div className="flex items-center gap-3">
						<button
							type="button"
							className={BTN_PRIMARY}
							onClick={handleCommit}
							data-testid="bulk-lp-upload-commit-btn"
						>
							Commit {preview.created} row{preview.created === 1 ? "" : "s"}
						</button>
						<button
							type="button"
							className={BTN_GHOST}
							onClick={handleReset}
							data-testid="bulk-lp-upload-cancel-btn"
						>
							Cancel
						</button>
					</div>
				</section>
			) : null}

			{/* ------------------ Step 3: Success ------------------ */}
			{step === "committed" ? (
				<section
					className={`${CARD} flex flex-col gap-4`}
					data-testid="bulk-lp-upload-success"
					aria-label="commit success"
				>
					{jobQuery.isLoading ? (
						<p
							className="text-sm text-zinc-600"
							data-testid="bulk-lp-upload-job-loading"
							role="status"
						>
							Loading committed job.
						</p>
					) : null}
					{jobQuery.error ? (
						<p className="text-sm text-red-600" role="alert">
							{jobQuery.error.message}
						</p>
					) : null}
					{jobQuery.data ? (
						<>
							<div className="flex flex-col gap-1">
								<h2 className="text-lg font-semibold text-emerald-700">
									Upload committed
								</h2>
								<p className="text-sm text-zinc-600">
									Job <code>{jobQuery.data.job_id}</code> — {jobQuery.data.created} landing pages
									attached to campaign <code>{jobQuery.data.campaign_id}</code>.
								</p>
							</div>

							{jobQuery.data.rows.length > 0 ? (
								<div
									className="max-h-80 overflow-auto rounded-[8px] border border-zinc-200"
									data-testid="bulk-lp-upload-rows-table"
								>
									<table className="w-full text-left text-sm">
										<thead className="sticky top-0 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
											<tr>
												<th className="px-3 py-2">#</th>
												<th className="px-3 py-2">Label</th>
												<th className="px-3 py-2">URL</th>
												<th className="px-3 py-2">Weight</th>
												<th className="px-3 py-2">Default</th>
											</tr>
										</thead>
										<tbody>
											{jobQuery.data.rows.map((r) => (
												<tr key={r.row_index} className="border-t border-zinc-100">
													<td className="px-3 py-2 font-mono text-xs text-zinc-700">
														{r.row_index}
													</td>
													<td className="px-3 py-2 text-zinc-900">{r.label}</td>
													<td className="px-3 py-2 text-zinc-600">
														<a
															href={r.url}
															className="text-sky-700 hover:underline"
															target="_blank"
															rel="noopener noreferrer"
														>
															{r.url}
														</a>
													</td>
													<td className="px-3 py-2 text-zinc-700">{r.weight}</td>
													<td className="px-3 py-2 text-zinc-700">
														{r.is_default ? (
															<span className={PILL_OK}>yes</span>
														) : (
															<span className={PILL_NEUTRAL}>no</span>
														)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : null}

							<div>
								<button
									type="button"
									className={BTN_GHOST}
									onClick={handleReset}
									data-testid="bulk-lp-upload-new-btn"
								>
									Upload another
								</button>
							</div>
						</>
					) : null}
				</section>
			) : null}

			{/* ------------------ Campaign history sidebar ------------------ */}
			{campaignId.trim().length > 0 && historyJobs.length > 0 ? (
				<aside
					className={`${CARD} flex flex-col gap-3`}
					data-testid="bulk-lp-upload-history"
					aria-label="recent jobs for this campaign"
				>
					<h3 className="text-sm font-semibold text-zinc-800">
						Recent jobs for {campaignId.trim()}
					</h3>
					<ul className="flex flex-col gap-2">
						{historyJobs.slice(0, 10).map((j) => (
							<li
								key={j.job_id}
								className="flex items-center justify-between rounded-[6px] border border-zinc-100 px-3 py-2 text-xs"
							>
								<code className="text-zinc-700">{j.job_id}</code>
								<div className="flex items-center gap-2">
									<span className={PILL_NEUTRAL}>parsed {j.parsed}</span>
									<span className={PILL_OK}>ok {j.created}</span>
									{j.error_count > 0 ? (
										<span className={PILL_ERR}>err {j.error_count}</span>
									) : null}
								</div>
							</li>
						))}
					</ul>
				</aside>
			) : null}
		</main>
	);
}

// ---------------------------------------------------------------------------
// Local bits — tiny stateless helpers
// ---------------------------------------------------------------------------

function StepPill({ label, active, done }: { label: string; active: boolean; done: boolean }) {
	const base = "rounded-full border px-3 py-1 text-xs font-medium";
	if (active) {
		return <li className={`${base} border-sky-300 bg-sky-50 text-sky-700`}>{label}</li>;
	}
	if (done) {
		return (
			<li className={`${base} border-emerald-300 bg-emerald-50 text-emerald-700`}>
				{label}
			</li>
		);
	}
	return <li className={`${base} border-zinc-200 bg-zinc-50 text-zinc-500`}>{label}</li>;
}

function StepSep() {
	return <li aria-hidden="true" className="h-px w-6 bg-zinc-200" />;
}

export default BulkLpUploadPage;
